// Chapter 7: South Dakota Crop Insurance Laws

const chapter7 = {
  id: 7,
  title: "South Dakota Crop Insurance Laws",
  sections: [
    {
      title: "Introduction to South Dakota Insurance Regulation",
      content: `
        <h2>Introduction to South Dakota Insurance Regulation</h2>

        <p>South Dakota regulates the business of insurance through state statutes (South Dakota Codified Laws) and administrative rules. Crop insurance agents must understand both federal requirements (from RMA/FCIC) and state laws governing insurance licensing, marketing practices, and agent conduct.</p>

        <div class="key-concept">
          <h3>Key Concept: Dual Regulation</h3>
          <p>Crop insurance agents are subject to DUAL regulatory oversight:</p>
          <ul>
            <li><strong>Federal Level:</strong> RMA/FCIC sets policy terms, premium rates, and program rules</li>
            <li><strong>State Level:</strong> South Dakota Division of Insurance regulates agent licensing, conduct, and marketing practices</li>
          </ul>
        </div>

        <h3>South Dakota Division of Insurance</h3>
        <p>The South Dakota Division of Insurance is the state agency responsible for regulating insurance in South Dakota. It operates under the Department of Labor and Regulation.</p>

        <div class="definition">
          <h4>Director of Insurance</h4>
          <p>The Director of Insurance (also called Commissioner in some states) is appointed by the Governor and is responsible for enforcing South Dakota insurance laws and protecting consumers.</p>
        </div>

        <h3>Powers of the Director</h3>
        <p>The Director of Insurance has broad authority to:</p>
        <ul>
          <li>Issue, suspend, revoke, or refuse to renew insurance licenses</li>
          <li>Examine insurance companies and agencies</li>
          <li>Investigate complaints against licensees</li>
          <li>Impose penalties for violations of insurance laws</li>
          <li>Adopt rules and regulations necessary to enforce insurance statutes</li>
          <li>Approve or disapprove insurance forms and rates (where applicable)</li>
          <li>Issue cease and desist orders</li>
          <li>Conduct hearings on licensing matters</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Remember that while RMA sets crop insurance policy terms, the STATE regulates the agent's license. The Director of Insurance can revoke your license for violating state insurance laws, even if you haven't violated any federal crop insurance rules.</p>
        </div>

        <h3>South Dakota Codified Laws (SDCL)</h3>
        <p>Insurance laws are primarily found in SDCL Title 58 (Insurance). Key chapters include:</p>
        <ul>
          <li><strong>Chapter 58-2:</strong> Department of Insurance organization</li>
          <li><strong>Chapter 58-30:</strong> Insurance producers (agents)</li>
          <li><strong>Chapter 58-33:</strong> Unfair trade practices</li>
          <li><strong>Chapter 58-11:</strong> General requirements for insurers</li>
        </ul>
      `
    },
    {
      title: "Insurance Producer Licensing Requirements",
      content: `
        <h2>Insurance Producer Licensing Requirements</h2>

        <p>In South Dakota, anyone who sells, solicits, or negotiates insurance must hold an insurance producer license issued by the Director of Insurance.</p>

        <div class="definition">
          <h4>Insurance Producer</h4>
          <p>A person required to be licensed under the laws of South Dakota to sell, solicit, or negotiate insurance. The term "producer" replaced the older terms "agent" and "broker" in most states.</p>
        </div>

        <h3>Types of Producer Licenses</h3>
        <p>South Dakota issues licenses by "line of authority." Crop insurance agents typically need:</p>

        <table>
          <tr>
            <th>License Type</th>
            <th>What It Covers</th>
          </tr>
          <tr>
            <td>Property</td>
            <td>Insurance for damage to property, including crop hail insurance</td>
          </tr>
          <tr>
            <td>Casualty</td>
            <td>Insurance against legal liability</td>
          </tr>
          <tr>
            <td>Crop</td>
            <td>Multi-Peril Crop Insurance and related federal programs</td>
          </tr>
        </table>

        <div class="key-concept">
          <h3>Key Concept: Separate Lines of Authority</h3>
          <p>A producer may hold licenses for multiple lines of authority. To sell BOTH private crop hail AND federal MPCI, an agent typically needs BOTH Property and Crop lines of authority.</p>
        </div>

        <h3>Initial Licensing Requirements</h3>
        <p>To obtain an insurance producer license in South Dakota, an applicant must:</p>
        <ol>
          <li><strong>Be at least 18 years old</strong></li>
          <li><strong>Be a U.S. citizen or legal resident</strong></li>
          <li><strong>Complete pre-licensing education</strong> (varies by line of authority)</li>
          <li><strong>Pass the state licensing examination</strong></li>
          <li><strong>Submit a license application</strong> with required fees</li>
          <li><strong>Pass a background check</strong></li>
          <li><strong>Not have been convicted of a disqualifying crime</strong></li>
        </ol>

        <div class="important">
          <h4>Important: Background Checks</h4>
          <p>South Dakota requires criminal background checks for all license applicants. Certain felony convictions—especially those involving fraud, dishonesty, or breach of trust—may result in license denial.</p>
        </div>

        <h3>Pre-Licensing Education</h3>
        <p>South Dakota requires completion of approved pre-licensing education before taking the licensing exam. The hours vary by line of authority:</p>
        <ul>
          <li>Property line: Specific hour requirements</li>
          <li>Casualty line: Specific hour requirements</li>
          <li>Crop line: May have different or additional requirements</li>
        </ul>

        <h3>Licensing Examinations</h3>
        <p>The state licensing exam tests knowledge of:</p>
        <ul>
          <li>Insurance concepts and terminology</li>
          <li>Types of policies and coverages</li>
          <li>Policy provisions</li>
          <li>South Dakota insurance laws and regulations</li>
          <li>Ethics and agent responsibilities</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>The licensing exam has a time limit and requires a passing score (typically 70%). You must pass the exam within a certain period after completing pre-licensing education.</p>
        </div>

        <h3>Resident vs. Non-Resident Licenses</h3>

        <div class="definition">
          <h4>Resident Producer</h4>
          <p>An individual whose home state (principal place of residence or business) is South Dakota.</p>
        </div>

        <div class="definition">
          <h4>Non-Resident Producer</h4>
          <p>An individual licensed in another state who wishes to transact insurance in South Dakota. Non-resident applicants must hold a valid license in their home state.</p>
        </div>

        <p>Non-resident producers may obtain a South Dakota license without examination if they hold a valid license in their home state and that state grants reciprocal privileges to South Dakota residents.</p>
      `
    },
    {
      title: "License Maintenance and Renewal",
      content: `
        <h2>License Maintenance and Renewal</h2>

        <p>Maintaining an active insurance license requires ongoing compliance with state requirements, including continuing education and timely renewal.</p>

        <h3>License Terms and Renewal</h3>
        <p>In South Dakota:</p>
        <ul>
          <li>Producer licenses are issued for a two-year term</li>
          <li>Licenses expire on the last day of the licensee's birth month in odd or even years (based on birth year)</li>
          <li>Renewal applications must be submitted before expiration</li>
          <li>Late renewals may be subject to additional fees or penalties</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Continuous Licensing</h3>
          <p>Letting your license lapse can result in loss of your book of business, appointments, and ability to earn commissions. Always renew before expiration!</p>
        </div>

        <h3>Continuing Education (CE) Requirements</h3>
        <p>South Dakota requires insurance producers to complete continuing education to maintain their licenses. Under <strong>SDCL 58-30-116</strong> and <strong>ARSD 20:06:18:03</strong>, the number of hours depends on which lines of authority the producer holds—not a single flat total for everyone. Note that the licensing term for the crop line is <strong>crop hail</strong>.</p>

        <table>
          <tr>
            <th>Lines of Authority Held</th>
            <th>CEC Hours Required per Two-Year Period</th>
          </tr>
          <tr>
            <td>Crop hail only</td>
            <td>4 hours certified crop hail</td>
          </tr>
          <tr>
            <td>Property/Casualty only</td>
            <td>10 hours certified P&amp;C</td>
          </tr>
          <tr>
            <td>Life/Health only</td>
            <td>10 hours certified Life/Health</td>
          </tr>
          <tr>
            <td>Crop hail and Property/Casualty</td>
            <td>10 hours total — 2 crop hail + 8 P&amp;C</td>
          </tr>
          <tr>
            <td>Crop hail and Life/Health</td>
            <td>10 hours total — 2 crop hail + 8 Life/Health</td>
          </tr>
          <tr>
            <td>Property/Casualty and Life/Health</td>
            <td>20 hours total — 10 P&amp;C + 10 Life/Health</td>
          </tr>
          <tr>
            <td>Crop hail, Property/Casualty and Life/Health</td>
            <td>20 hours total — 2 crop hail + 8 P&amp;C + 10 Life/Health</td>
          </tr>
          <tr>
            <td>Completion Deadline</td>
            <td>Biennially, by the last day of the licensee's birth month</td>
          </tr>
          <tr>
            <td>Carryover</td>
            <td>Prohibited — no hours carry from one two-year period to the next (ARSD 20:06:18:18)</td>
          </tr>
        </table>

        <div class="key-concept">
          <h3>Key Concept: The 20-Hour Cap</h3>
          <p>SDCL 58-30-116 provides that no more than <strong>20 hours</strong> of CE may be required of any licensee, no matter how many qualifications are held. Only 2 crop hail hours ever count toward a combined requirement.</p>
        </div>

        <div class="important">
          <h4>Important: No State Ethics Requirement</h4>
          <p>South Dakota does <strong>not</strong> impose a separate ethics-hour requirement. CE hours are broken out by line of authority only. Ethics appears in ARSD 20:06:18:08 merely as one of many <em>eligible</em> course subjects—it is never a mandated minimum.</p>
        </div>

        <div class="important">
          <h4>Separate Federal Requirement for Crop Agents</h4>
          <p>Agents selling federally reinsured crop insurance must also complete <strong>3 hours of training per year</strong> under federal (RMA/SRA) requirements. This is in addition to—and separate from—South Dakota's state CE.</p>
        </div>

        <h3>Approved CE Providers and Courses</h3>
        <p>CE courses must be:</p>
        <ul>
          <li>Approved by the South Dakota Division of Insurance</li>
          <li>Relevant to the lines of authority held</li>
          <li>Completed through approved providers</li>
          <li>Documented with completion certificates</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Keep records of all CE completions for at least 4 years. You may be audited and required to provide proof of completion.</p>
        </div>

        <h3>Address and Name Changes</h3>
        <p>Producers must notify the Director of Insurance within 30 days of any change in:</p>
        <ul>
          <li>Legal name</li>
          <li>Residence address</li>
          <li>Business address</li>
          <li>Email address</li>
          <li>Telephone number</li>
        </ul>

        <h3>Reporting Requirements</h3>
        <p>Producers must report certain events to the Director, including:</p>
        <ul>
          <li>Criminal charges or convictions</li>
          <li>Administrative actions in other states</li>
          <li>Civil judgments related to insurance activities</li>
          <li>Denial, suspension, or revocation of a license in another jurisdiction</li>
        </ul>

        <div class="important">
          <h4>Important: Timely Reporting</h4>
          <p>Failure to report required events within the specified timeframe (typically 30 days) is itself a violation that can result in disciplinary action.</p>
        </div>
      `
    },
    {
      title: "Agent Appointments",
      content: `
        <h2>Agent Appointments</h2>

        <p>In addition to holding a license, producers must be "appointed" by insurance companies before they can represent those companies and sell their products.</p>

        <div class="definition">
          <h4>Appointment</h4>
          <p>The authorization by an insurer for a licensed producer to act as its agent. An appointment creates an agency relationship between the producer and the insurance company.</p>
        </div>

        <h3>Appointment Process</h3>
        <p>The typical appointment process includes:</p>
        <ol>
          <li>Producer applies to an insurance company for appointment</li>
          <li>Company evaluates the producer's qualifications, experience, and background</li>
          <li>If approved, the company files the appointment with the state</li>
          <li>The Director records the appointment</li>
          <li>Producer can now represent that company</li>
        </ol>

        <h3>For Crop Insurance</h3>
        <p>To sell federal crop insurance, a producer must be appointed by an Approved Insurance Provider (AIP). The appointment must be recorded with both:</p>
        <ul>
          <li>The State Division of Insurance</li>
          <li>RMA/FCIC (for access to federal systems)</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: No License = No Appointment</h3>
          <p>A company cannot appoint an unlicensed individual. The producer license must be in force before an appointment can be made.</p>
        </div>

        <h3>Appointment Fees</h3>
        <p>Insurance companies typically pay appointment fees to the state when appointing producers. These fees help fund the Division of Insurance.</p>

        <h3>Termination of Appointments</h3>
        <p>Appointments may be terminated by:</p>
        <ul>
          <li><strong>The producer:</strong> Voluntarily resigning</li>
          <li><strong>The insurer:</strong> For cause (misconduct) or without cause (business reasons)</li>
          <li><strong>Operation of law:</strong> License revocation, company insolvency, etc.</li>
        </ul>

        <div class="important">
          <h4>Important: Notification of Termination for Cause</h4>
          <p>If an insurer terminates a producer's appointment for cause (misconduct, fraud, etc.), the insurer must notify the Director of Insurance within 30 days and provide the reasons for termination.</p>
        </div>

        <h3>Multiple Appointments</h3>
        <p>In South Dakota, producers may hold appointments with multiple insurance companies simultaneously. This allows producers to:</p>
        <ul>
          <li>Offer products from various carriers</li>
          <li>Find the best coverage options for clients</li>
          <li>Compare rates and terms across companies</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>While producers may be appointed by multiple companies, they must always act in the best interest of the client and disclose any relationships that might affect their recommendations.</p>
        </div>
      `
    },
    {
      title: "Unfair Trade Practices",
      content: `
        <h2>Unfair Trade Practices</h2>

        <p>South Dakota prohibits various unfair trade practices in the business of insurance. These laws protect consumers and maintain the integrity of the insurance marketplace.</p>

        <div class="key-concept">
          <h3>Key Concept: Unfair Trade Practices Act</h3>
          <p>SDCL Chapter 58-33 defines and prohibits unfair methods of competition and unfair or deceptive acts in the insurance business. Violations can result in license revocation, fines, and other penalties.</p>
        </div>

        <h3>Prohibited Practices</h3>

        <h4>1. Misrepresentation</h4>
        <div class="definition">
          <h4>Misrepresentation</h4>
          <p>Making false or misleading statements about insurance policies, benefits, terms, or conditions to induce someone to purchase, lapse, forfeit, or surrender insurance.</p>
        </div>

        <p>Examples of misrepresentation:</p>
        <ul>
          <li>Falsely stating that a policy covers perils it does not</li>
          <li>Misrepresenting the financial condition of an insurer</li>
          <li>Making misleading comparisons between policies</li>
          <li>Misrepresenting dividend projections or policy values</li>
        </ul>

        <h4>2. False Advertising</h4>
        <p>Making, publishing, or disseminating statements that are untrue, deceptive, or misleading about insurance products or services.</p>

        <h4>3. Defamation</h4>
        <p>Making false statements that damage the reputation of another insurer, agent, or their products.</p>

        <h4>4. Unfair Discrimination</h4>
        <p>Treating similarly situated insureds differently without actuarial justification. This includes discriminating based on race, religion, national origin, or other protected characteristics.</p>

        <div class="important">
          <h4>Important: Actuarial vs. Unfair Discrimination</h4>
          <p>Insurance companies may use actuarially justified factors (like different crops or counties having different risks) to set rates. This is NOT unfair discrimination. Unfair discrimination occurs when insureds with similar risk profiles are treated differently without justification.</p>
        </div>

        <h4>5. Rebating</h4>
        <div class="definition">
          <h4>Rebating</h4>
          <p>Giving or offering to give anything of value not specified in the policy as an inducement to purchase insurance.</p>
        </div>

        <p>Examples of prohibited rebating:</p>
        <ul>
          <li>Returning part of the commission to the insured</li>
          <li>Paying for the insured's premium from personal funds</li>
          <li>Giving gifts of significant value to secure business</li>
          <li>Offering special deals not available to other policyholders</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Rebating is ILLEGAL in South Dakota. Even if both parties agree, a producer cannot rebate. Small promotional items (pens, calendars) may be acceptable, but anything of substantial value is prohibited.</p>
        </div>

        <h4>6. Twisting</h4>
        <div class="definition">
          <h4>Twisting</h4>
          <p>Inducing a policyholder to lapse, surrender, or replace existing insurance through misrepresentation or incomplete comparison for the purpose of earning a commission.</p>
        </div>

        <h4>7. Churning</h4>
        <div class="definition">
          <h4>Churning</h4>
          <p>Excessive replacement of policies primarily to generate commissions rather than benefit the insured. Similar to twisting but often involves the same agent replacing their own client's policies.</p>
        </div>
      `
    },
    {
      title: "Additional Prohibited Practices",
      content: `
        <h2>Additional Prohibited Practices</h2>

        <h3>Controlled Business</h3>
        <div class="definition">
          <h4>Controlled Business</h4>
          <p>Insurance written primarily on the lives or property of the producer, their immediate family, employer, or employees. While some controlled business is normal, a license obtained primarily to write controlled business may be grounds for denial or revocation.</p>
        </div>

        <h3>Inducements and Special Deals</h3>
        <p>Producers cannot offer special advantages not available to all policyholders, including:</p>
        <ul>
          <li>Reduced premiums not reflected in filed rates</li>
          <li>Additional coverage at no cost</li>
          <li>Promises of future benefits not in the policy</li>
          <li>Splitting commissions with unlicensed persons</li>
        </ul>

        <h3>Fiduciary Responsibilities</h3>
        <div class="key-concept">
          <h3>Key Concept: Premium Funds Are Trust Funds</h3>
          <p>Premium funds collected by a producer belong to the insurer (or insured for refunds) and must be held in trust. Misappropriation of premium funds is a serious violation that can result in criminal prosecution.</p>
        </div>

        <p>Producers must:</p>
        <ul>
          <li>Maintain accurate records of all premium transactions</li>
          <li>Remit premiums to insurers in a timely manner</li>
          <li>Keep premium funds separate from personal funds</li>
          <li>Never use premium funds for personal expenses</li>
        </ul>

        <h3>Commingling Funds</h3>
        <div class="definition">
          <h4>Commingling</h4>
          <p>Mixing premium funds with personal or business operating funds. This is prohibited because it makes it difficult to account for trust funds and can lead to misuse.</p>
        </div>

        <div class="important">
          <h4>Important: Trust Account Requirements</h4>
          <p>Many agencies are required to maintain a separate premium trust account to hold client funds. Failure to properly maintain trust accounts is grounds for disciplinary action.</p>
        </div>

        <h3>Fraud</h3>
        <p>Insurance fraud includes any intentional deception made for financial gain in connection with insurance. This includes:</p>
        <ul>
          <li>Submitting false applications</li>
          <li>Inflating claims</li>
          <li>Staging losses</li>
          <li>Misrepresenting material facts</li>
          <li>Forging signatures</li>
        </ul>

        <p>Insurance fraud is a crime in South Dakota, punishable by fines, imprisonment, or both.</p>

        <h3>Tied Selling</h3>
        <p>Requiring the purchase of one type of insurance as a condition of selling another type is generally prohibited, unless the products are naturally connected.</p>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Know the differences between misrepresentation, rebating, twisting, and churning. These are commonly tested concepts on the state exam.</p>
        </div>
      `
    },
    {
      title: "Crop Insurance Marketing Practices",
      content: `
        <h2>Crop Insurance Marketing Practices</h2>

        <p>Federal crop insurance has specific marketing regulations established by RMA in addition to state laws. Crop insurance agents must comply with both sets of rules.</p>

        <h3>RMA Marketing Regulations</h3>
        <p>The Standard Reinsurance Agreement (SRA) between AIPs and FCIC includes specific requirements for marketing crop insurance:</p>

        <div class="key-concept">
          <h3>Key Concept: Agent Compensation</h3>
          <p>Agent compensation (commissions) for federal crop insurance is set by agreement between the agent and the AIP, within limits established by RMA. Agents CANNOT receive compensation from the insured farmer.</p>
        </div>

        <h3>Prohibited Marketing Activities</h3>
        <p>Under federal regulations and state law, crop insurance agents CANNOT:</p>
        <ul>
          <li><strong>Rebate premiums:</strong> Return any portion of premium to the insured</li>
          <li><strong>Pay for crops:</strong> Provide anything of value related to commodity purchases as an inducement</li>
          <li><strong>Give gifts:</strong> Provide items of more than nominal value to secure business</li>
          <li><strong>Make conditional arrangements:</strong> Promise something in return for purchasing insurance</li>
          <li><strong>Pay expenses:</strong> Pay for farmer's meals, travel, or other expenses as an inducement</li>
        </ul>

        <h3>Acceptable Marketing Practices</h3>
        <p>Crop insurance agents CAN:</p>
        <ul>
          <li>Provide educational materials about crop insurance</li>
          <li>Give promotional items of nominal value (pens, calendars, etc.)</li>
          <li>Attend farm shows and set up educational displays</li>
          <li>Advertise their services (truthfully)</li>
          <li>Compare coverage options across policies</li>
          <li>Provide good customer service</li>
        </ul>

        <div class="example">
          <h4>Example: What's Allowed vs. Prohibited</h4>
          <table>
            <tr>
              <th>Scenario</th>
              <th>Allowed?</th>
            </tr>
            <tr>
              <td>Giving a farmer a pen with your contact info</td>
              <td>Yes - nominal value</td>
            </tr>
            <tr>
              <td>Taking a prospect to an expensive dinner</td>
              <td>No - inducement</td>
            </tr>
            <tr>
              <td>Providing a coverage comparison spreadsheet</td>
              <td>Yes - educational</td>
            </tr>
            <tr>
              <td>Paying part of the farmer's premium</td>
              <td>No - rebating</td>
            </tr>
            <tr>
              <td>Having a booth at a county fair</td>
              <td>Yes - marketing</td>
            </tr>
            <tr>
              <td>Giving a gift card in exchange for a policy</td>
              <td>No - inducement</td>
            </tr>
          </table>
        </div>

        <div class="important">
          <h4>Important: FCIC Penalties</h4>
          <p>Violations of federal marketing rules can result in sanctions from FCIC/RMA, including termination from the crop insurance program—in addition to any state penalties.</p>
        </div>

        <h3>Competition and Agent Selection</h3>
        <p>Farmers have the right to choose their crop insurance agent. Agents competing for business must do so based on:</p>
        <ul>
          <li>Quality of service</li>
          <li>Knowledge and expertise</li>
          <li>Reputation and trustworthiness</li>
          <li>Availability and responsiveness</li>
        </ul>

        <p>Agents should NOT compete based on illegal inducements or by disparaging other agents.</p>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>The "nominal value" threshold for promotional items is generally interpreted to mean items under $10-20 in value. Anything that could reasonably be seen as an inducement to purchase insurance is prohibited.</p>
        </div>
      `
    },
    {
      title: "Agent Duties and Responsibilities",
      content: `
        <h2>Agent Duties and Responsibilities</h2>

        <p>Insurance producers have legal and ethical duties to both the insurers they represent and the clients they serve. Understanding these duties is essential for proper agent conduct.</p>

        <h3>Duties to Clients</h3>

        <div class="key-concept">
          <h3>Key Concept: Good Faith and Fair Dealing</h3>
          <p>Agents must act in good faith in all dealings with clients. This means being honest, transparent, and acting with the client's interests in mind—even when not legally required to do so.</p>
        </div>

        <h4>Duty of Competence</h4>
        <p>Agents must have sufficient knowledge and skill to:</p>
        <ul>
          <li>Explain policy coverages accurately</li>
          <li>Identify client needs</li>
          <li>Recommend appropriate coverage</li>
          <li>Answer questions correctly</li>
          <li>Process applications properly</li>
        </ul>

        <h4>Duty of Disclosure</h4>
        <p>Agents must disclose material information that affects the client's decision, including:</p>
        <ul>
          <li>Policy limitations and exclusions</li>
          <li>Any conflicts of interest</li>
          <li>Relationships with insurers</li>
          <li>How compensation is earned</li>
        </ul>

        <h4>Duty to Obtain Requested Coverage</h4>
        <p>When a client requests specific coverage, the agent must either:</p>
        <ul>
          <li>Obtain that coverage, or</li>
          <li>Inform the client that coverage is unavailable or has been declined</li>
        </ul>

        <div class="important">
          <h4>Important: Failure to Obtain Coverage</h4>
          <p>If an agent fails to obtain requested coverage and doesn't notify the client, the agent may be liable for damages the client suffers from the coverage gap.</p>
        </div>

        <h3>Duties to Insurers</h3>

        <h4>Agency Relationship</h4>
        <p>When appointed by an insurer, the agent becomes the insurer's representative. This creates duties including:</p>
        <ul>
          <li><strong>Loyalty:</strong> Acting in the insurer's best interests</li>
          <li><strong>Obedience:</strong> Following the insurer's lawful instructions</li>
          <li><strong>Accounting:</strong> Properly handling and remitting premiums</li>
          <li><strong>Notice:</strong> Communicating material information to the insurer</li>
        </ul>

        <h4>Binding Authority</h4>
        <div class="definition">
          <h4>Binding Authority</h4>
          <p>The authority given to an agent to accept risks and commit the insurer to provide coverage before the policy is actually issued.</p>
        </div>

        <p>For crop insurance, agents generally have authority to:</p>
        <ul>
          <li>Accept applications</li>
          <li>Provide policy documents</li>
          <li>Make authorized changes to policies</li>
        </ul>

        <p>Agents should NOT exceed their binding authority—doing so can expose both the agent and the insurer to liability.</p>

        <h3>Record Keeping Requirements</h3>
        <p>Agents must maintain accurate records including:</p>
        <ul>
          <li>Copies of applications and policy documents</li>
          <li>Premium receipts and payment records</li>
          <li>Client correspondence</li>
          <li>Claims information</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Records should generally be kept for at least 3-7 years (check specific requirements). Good record keeping protects both the agent and the client in case of disputes.</p>
        </div>
      `
    },
    {
      title: "Privacy and Confidentiality",
      content: `
        <h2>Privacy and Confidentiality</h2>

        <p>Insurance agents have access to sensitive personal and financial information about their clients. State and federal laws require protection of this information.</p>

        <h3>Types of Protected Information</h3>
        <p>In crop insurance, protected information may include:</p>
        <ul>
          <li>Personal identifying information (SSN, date of birth)</li>
          <li>Farm financial information</li>
          <li>Production history records</li>
          <li>Acreage and yield data</li>
          <li>Claim history</li>
          <li>Bank account information</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Gramm-Leach-Bliley Act (GLBA)</h3>
          <p>Federal law requires insurance companies and agents to provide privacy notices to customers and protect nonpublic personal information (NPI) from unauthorized disclosure.</p>
        </div>

        <h3>Privacy Notice Requirements</h3>
        <p>Under GLBA, insurers must provide privacy notices that explain:</p>
        <ul>
          <li>What information is collected</li>
          <li>How information is used</li>
          <li>What information is shared and with whom</li>
          <li>How information is protected</li>
          <li>Customer rights regarding their information</li>
        </ul>

        <h3>Agent Responsibilities</h3>
        <p>Agents must:</p>
        <ul>
          <li>Keep client information confidential</li>
          <li>Use information only for authorized insurance purposes</li>
          <li>Not disclose information without proper authorization</li>
          <li>Protect records from unauthorized access</li>
          <li>Properly dispose of records containing sensitive information</li>
        </ul>

        <div class="important">
          <h4>Important: Information Sharing</h4>
          <p>Agents may share client information with the insurer for underwriting and claims purposes, but sharing with other parties (such as other farmers or competitors) is prohibited without consent.</p>
        </div>

        <h3>Crop Insurance Specific Privacy</h3>
        <p>Production data and claim information are particularly sensitive in crop insurance because:</p>
        <ul>
          <li>Competitors could use yield information for business advantage</li>
          <li>Claim history could affect reputation in the farming community</li>
          <li>Financial information reveals farm profitability</li>
        </ul>

        <div class="example">
          <h4>Example: Privacy Violation</h4>
          <p>An agent tells a neighboring farmer that his client "had a huge claim last year because his yields were terrible." This violates the client's privacy by disclosing both production information and claim history without authorization.</p>
        </div>

        <h3>Data Security</h3>
        <p>Agents should implement appropriate safeguards:</p>
        <ul>
          <li>Secure storage of physical records</li>
          <li>Password protection for electronic files</li>
          <li>Secure disposal (shredding) of documents</li>
          <li>Caution when discussing client matters in public</li>
          <li>Secure transmission of sensitive information</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Privacy violations can result in both civil penalties under state law and federal penalties under GLBA. Always err on the side of protecting client information.</p>
        </div>
      `
    },
    {
      title: "License Denial, Suspension, and Revocation",
      content: `
        <h2>License Denial, Suspension, and Revocation</h2>

        <p>The Director of Insurance has authority to deny, suspend, or revoke producer licenses for various causes. Understanding these grounds helps agents maintain compliance and protect their licenses.</p>

        <h3>Grounds for License Action</h3>
        <p>The Director may deny, suspend, revoke, or refuse to renew a license for:</p>

        <h4>Criminal History</h4>
        <ul>
          <li>Conviction of a felony</li>
          <li>Conviction of a misdemeanor involving fraud, dishonesty, or breach of trust</li>
          <li>Criminal conduct that demonstrates unfitness to hold a license</li>
        </ul>

        <h4>Regulatory Violations</h4>
        <ul>
          <li>Violation of insurance laws or regulations</li>
          <li>Violation of any insurance law in another jurisdiction</li>
          <li>Material misrepresentation on the license application</li>
          <li>Obtaining or attempting to obtain a license through fraud</li>
        </ul>

        <h4>Professional Misconduct</h4>
        <ul>
          <li>Misappropriation or conversion of funds</li>
          <li>Fraudulent acts in insurance transactions</li>
          <li>Demonstrating incompetence or untrustworthiness</li>
          <li>Having appointments terminated for cause by insurers</li>
          <li>Failure to comply with administrative orders</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Grounds Are Cumulative</h3>
          <p>A single serious violation can result in license revocation, but the Director may also consider patterns of smaller violations that together demonstrate unfitness.</p>
        </div>

        <h3>Types of License Actions</h3>

        <table>
          <tr>
            <th>Action</th>
            <th>Description</th>
          </tr>
          <tr>
            <td>Denial</td>
            <td>Refusal to issue a license to an applicant</td>
          </tr>
          <tr>
            <td>Suspension</td>
            <td>Temporary prohibition from acting as a producer</td>
          </tr>
          <tr>
            <td>Revocation</td>
            <td>Permanent cancellation of the license</td>
          </tr>
          <tr>
            <td>Non-Renewal</td>
            <td>Refusal to renew an expiring license</td>
          </tr>
          <tr>
            <td>Probation</td>
            <td>License continues under specific conditions</td>
          </tr>
        </table>

        <h3>Penalties and Fines</h3>
        <p>In addition to license action, the Director may impose:</p>
        <ul>
          <li><strong>Civil Penalties:</strong> Monetary fines up to specified amounts per violation</li>
          <li><strong>Cease and Desist Orders:</strong> Orders to stop specific conduct</li>
          <li><strong>Restitution:</strong> Repayment to injured parties</li>
        </ul>

        <div class="important">
          <h4>Important: Each Act = Separate Violation</h4>
          <p>Each instance of prohibited conduct may be treated as a separate violation with separate penalties. Multiple violations can result in substantial cumulative fines.</p>
        </div>

        <h3>Due Process Rights</h3>
        <p>Before taking action, the Director generally must:</p>
        <ul>
          <li>Provide written notice of the proposed action</li>
          <li>State the grounds for the action</li>
          <li>Give the licensee opportunity for a hearing</li>
          <li>Allow the licensee to present evidence and witnesses</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Emergency suspension may occur WITHOUT prior hearing if public protection requires immediate action. A hearing would then follow.</p>
        </div>
      `
    },
    {
      title: "Administrative Hearings and Appeals",
      content: `
        <h2>Administrative Hearings and Appeals</h2>

        <p>When the Director proposes to deny, suspend, or revoke a license, the applicant or licensee has the right to an administrative hearing. Understanding this process is important for protecting your rights.</p>

        <h3>The Hearing Process</h3>

        <h4>1. Notice of Hearing</h4>
        <p>The Director provides written notice including:</p>
        <ul>
          <li>The proposed action (denial, suspension, revocation)</li>
          <li>The specific grounds/charges</li>
          <li>The date, time, and place of hearing</li>
          <li>A statement of rights</li>
        </ul>

        <h4>2. Pre-Hearing Preparation</h4>
        <p>The licensee may:</p>
        <ul>
          <li>Review evidence against them</li>
          <li>Obtain legal representation</li>
          <li>Gather supporting documents and witnesses</li>
          <li>Submit written statements</li>
        </ul>

        <h4>3. The Hearing</h4>
        <p>Administrative hearings are conducted by a hearing examiner and include:</p>
        <ul>
          <li>Opening statements</li>
          <li>Presentation of evidence by the Division</li>
          <li>Presentation of defense</li>
          <li>Cross-examination of witnesses</li>
          <li>Closing arguments</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Burden of Proof</h3>
          <p>The Division typically bears the burden of proving the violations by a preponderance of the evidence (more likely than not). However, in some cases, the licensee may need to prove their fitness to hold a license.</p>
        </div>

        <h4>4. Decision</h4>
        <p>After the hearing, the Director issues a written decision including:</p>
        <ul>
          <li>Findings of fact</li>
          <li>Conclusions of law</li>
          <li>The action to be taken</li>
          <li>Any penalties or conditions</li>
        </ul>

        <h3>Appeals</h3>
        <p>If dissatisfied with the Director's decision, the licensee may appeal to:</p>
        <ol>
          <li>Circuit Court (judicial review of administrative action)</li>
          <li>Higher courts if necessary</li>
        </ol>

        <h3>Judicial Review Standard</h3>
        <p>Courts reviewing administrative decisions typically determine whether:</p>
        <ul>
          <li>The decision was supported by substantial evidence</li>
          <li>The decision was arbitrary or capricious</li>
          <li>The agency followed proper procedures</li>
          <li>The agency acted within its authority</li>
        </ul>

        <div class="important">
          <h4>Important: Time Limits</h4>
          <p>Appeals must be filed within specific time limits (often 30 days). Missing the deadline can waive your right to appeal.</p>
        </div>

        <h3>Representation</h3>
        <p>While licensees may represent themselves at administrative hearings, legal representation is strongly recommended when:</p>
        <ul>
          <li>License revocation is at stake</li>
          <li>Significant fines are proposed</li>
          <li>Complex legal issues are involved</li>
          <li>Criminal charges may follow</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>Know that licensees have due process rights including notice, the opportunity to be heard, and the right to appeal. These protections apply before a license can be revoked.</p>
        </div>
      `
    },
    {
      title: "Compliance Best Practices",
      content: `
        <h2>Compliance Best Practices</h2>

        <p>Maintaining compliance with insurance regulations protects both your license and your clients. Following best practices helps prevent violations and demonstrates professionalism.</p>

        <h3>Documentation Best Practices</h3>
        <ul>
          <li><strong>Document everything:</strong> Keep records of all client communications and transactions</li>
          <li><strong>Use checklists:</strong> Ensure all required steps are completed for each policy</li>
          <li><strong>Get signatures:</strong> Have clients sign important documents and acknowledgments</li>
          <li><strong>Store securely:</strong> Maintain organized, secure files for required retention periods</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: If It's Not Documented, It Didn't Happen</h3>
          <p>In disputes, regulators and courts rely on documentation. Verbal agreements or explanations may not be credible without supporting records.</p>
        </div>

        <h3>Continuing Education</h3>
        <ul>
          <li>Complete CE well before the deadline</li>
          <li>Choose courses relevant to your practice</li>
          <li>Keep certificates and completion records</li>
          <li>Consider CE as an opportunity to improve, not just a requirement</li>
        </ul>

        <h3>Ethical Decision Making</h3>
        <p>When facing ethical dilemmas, consider:</p>
        <ul>
          <li>Is it legal?</li>
          <li>Is it ethical?</li>
          <li>Would I be comfortable if it were public?</li>
          <li>What would a reasonable person think?</li>
          <li>When in doubt, disclose and get guidance</li>
        </ul>

        <div class="example">
          <h4>Example: Ethical Decision Making</h4>
          <p>A farmer offers to take you hunting in exchange for handling his crop insurance. While hunting is legal and farmers often hunt together, accepting this as an inducement for insurance business would be an ethical violation. Decline politely and explain why.</p>
        </div>

        <h3>Staying Current</h3>
        <p>Insurance regulations change frequently. Stay current by:</p>
        <ul>
          <li>Reading industry publications</li>
          <li>Attending association meetings</li>
          <li>Subscribing to RMA updates</li>
          <li>Taking relevant continuing education</li>
          <li>Consulting with compliance experts</li>
        </ul>

        <h3>When Problems Arise</h3>
        <p>If you become aware of a potential violation:</p>
        <ul>
          <li><strong>Don't ignore it:</strong> Problems usually get worse if unaddressed</li>
          <li><strong>Document what happened:</strong> Record the facts objectively</li>
          <li><strong>Seek guidance:</strong> Consult with supervisors, compliance officers, or legal counsel</li>
          <li><strong>Report if required:</strong> Some violations must be self-reported</li>
          <li><strong>Take corrective action:</strong> Fix the problem and prevent recurrence</li>
        </ul>

        <div class="important">
          <h4>Important: Voluntary Disclosure</h4>
          <p>In many cases, self-reporting violations before they're discovered can result in lesser penalties. Attempting to cover up violations typically makes matters worse.</p>
        </div>

        <h3>Building a Compliance Culture</h3>
        <p>For agencies:</p>
        <ul>
          <li>Establish clear policies and procedures</li>
          <li>Train all staff on compliance requirements</li>
          <li>Monitor for compliance regularly</li>
          <li>Address problems promptly</li>
          <li>Lead by example</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>The exam may present scenarios where you must identify the proper ethical or compliant course of action. Apply the principles: follow the law, act in the client's interest, be honest, and when in doubt, seek guidance.</p>
        </div>
      `
    },
    {
      title: "Federal and State Coordination",
      content: `
        <h2>Federal and State Coordination</h2>

        <p>Crop insurance agents operate at the intersection of federal and state regulations. Understanding how these regulatory systems interact is essential for compliance.</p>

        <h3>Federal Regulatory Framework</h3>
        <p>The federal crop insurance program is governed by:</p>
        <ul>
          <li><strong>Federal Crop Insurance Act:</strong> The enabling legislation</li>
          <li><strong>Code of Federal Regulations (CFR):</strong> Detailed program rules</li>
          <li><strong>RMA Handbooks and Procedures:</strong> Operational guidance</li>
          <li><strong>Standard Reinsurance Agreement:</strong> Contract between FCIC and AIPs</li>
          <li><strong>Appendix III:</strong> Agent compensation and conduct rules</li>
        </ul>

        <h3>State Regulatory Framework</h3>
        <p>State regulation covers:</p>
        <ul>
          <li><strong>Producer licensing:</strong> Who can sell insurance</li>
          <li><strong>Trade practices:</strong> How insurance can be marketed</li>
          <li><strong>Agent conduct:</strong> Standards of behavior</li>
          <li><strong>Consumer protection:</strong> Rights of policyholders</li>
          <li><strong>Enforcement:</strong> Penalties for violations</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Complementary Regulation</h3>
          <p>Federal and state regulations are complementary, not contradictory. Federal rules set the terms of crop insurance policies; state rules govern how agents operate. Both apply simultaneously.</p>
        </div>

        <h3>Areas of Overlap</h3>

        <table>
          <tr>
            <th>Area</th>
            <th>Federal Role</th>
            <th>State Role</th>
          </tr>
          <tr>
            <td>Agent Qualification</td>
            <td>RMA certification requirements</td>
            <td>Licensing examination and CE</td>
          </tr>
          <tr>
            <td>Marketing Practices</td>
            <td>SRA/Appendix III rules</td>
            <td>Unfair trade practices laws</td>
          </tr>
          <tr>
            <td>Fraud Prevention</td>
            <td>FCIC program integrity</td>
            <td>State insurance fraud laws</td>
          </tr>
          <tr>
            <td>Enforcement</td>
            <td>RMA sanctions, debarment</td>
            <td>License revocation, fines</td>
          </tr>
        </table>

        <h3>Consequences of Non-Compliance</h3>
        <p>A violation may trigger enforcement from multiple sources:</p>

        <div class="example">
          <h4>Example: Rebating Violation</h4>
          <p>An agent who rebates premium to a farmer may face:</p>
          <ul>
            <li><strong>Federal:</strong> Suspension from the crop insurance program by RMA</li>
            <li><strong>AIP:</strong> Termination of appointment</li>
            <li><strong>State:</strong> License suspension/revocation and fines</li>
            <li><strong>Civil:</strong> Potential lawsuit from affected parties</li>
          </ul>
        </div>

        <h3>Reporting Between Agencies</h3>
        <p>Federal and state agencies share information about violations:</p>
        <ul>
          <li>States report agent problems to other states through NIPR</li>
          <li>RMA receives notice of state license actions</li>
          <li>States receive notice of federal sanctions</li>
        </ul>

        <div class="important">
          <h4>Important: No Hiding</h4>
          <p>You cannot escape consequences by moving to another state or switching AIPs. Regulatory systems are interconnected, and problems follow you.</p>
        </div>

        <h3>Best Practice: Know Both Sets of Rules</h3>
        <p>Successful crop insurance agents:</p>
        <ul>
          <li>Understand both federal crop insurance rules and state insurance laws</li>
          <li>Follow the stricter requirement when they differ</li>
          <li>Stay current as both sets of rules evolve</li>
          <li>Seek clarification when requirements are unclear</li>
        </ul>

        <div class="exam-tip">
          <h4>Exam Tip</h4>
          <p>The state exam focuses on state laws, but remember that crop insurance agents must comply with BOTH federal and state requirements. When asked about proper conduct, apply both frameworks.</p>
        </div>
      `
    }
  ],
  quizQuestions: [
    {
      question: "Which state agency regulates insurance producer licensing in South Dakota?",
      options: [
        "Department of Agriculture",
        "Division of Insurance under the Department of Labor and Regulation",
        "Secretary of State's Office",
        "Risk Management Agency"
      ],
      correct: 1,
      explanation: "The South Dakota Division of Insurance, under the Department of Labor and Regulation, regulates insurance producer licensing, conduct, and trade practices in the state."
    },
    {
      question: "A licensee holds only a crop hail qualification in South Dakota. What is the continuing education requirement per two-year period?",
      options: [
        "4 CEC hours in certified crop hail courses",
        "10 CEC hours in certified crop hail courses",
        "24 CEC hours, including 3 hours of ethics",
        "No continuing education is required"
      ],
      correct: 0,
      explanation: "Under SDCL 58-30-116(2) and ARSD 20:06:18:03(3), a licensee holding only the crop hail qualification must obtain a minimum of 4 CEC hours certified for that line, every two years, due the last day of the licensee's birth month. South Dakota has no ethics-hour requirement. Separately, agents selling federally reinsured crop insurance must meet a federal training requirement of 3 hours per year."
    },
    {
      question: "An agent gives a farmer a $100 gift card as a 'thank you' for purchasing crop insurance. This is an example of:",
      options: [
        "Good customer service",
        "Rebating",
        "Twisting",
        "Churning"
      ],
      correct: 1,
      explanation: "Giving anything of substantial value as an inducement to purchase insurance is rebating, which is illegal in South Dakota. Items must be of nominal value only (like pens or calendars)."
    },
    {
      question: "Which of the following is considered 'twisting'?",
      options: [
        "Explaining policy differences to help a client choose",
        "Inducing a policyholder to replace insurance through misrepresentation",
        "Selling insurance to family members",
        "Charging the rate filed with the state"
      ],
      correct: 1,
      explanation: "Twisting is inducing a policyholder to lapse, surrender, or replace existing insurance through misrepresentation or incomplete comparison, typically to earn a commission."
    },
    {
      question: "What is the difference between moral hazard and morale hazard?",
      options: [
        "There is no difference; they are the same thing",
        "Moral hazard is carelessness; morale hazard is intentional dishonesty",
        "Moral hazard is intentional dishonesty; morale hazard is carelessness due to insurance",
        "Both refer only to physical conditions that increase risk"
      ],
      correct: 2,
      explanation: "Moral hazard involves intentional dishonesty to cause or exaggerate a loss. Morale hazard is carelessness or indifference because insurance coverage exists."
    },
    {
      question: "How long must a producer notify the Director of a change of address?",
      options: [
        "10 days",
        "30 days",
        "60 days",
        "90 days"
      ],
      correct: 1,
      explanation: "Producers must notify the Director of Insurance within 30 days of any change in name, residence address, business address, or other required information."
    },
    {
      question: "An insurance producer is appointed by an insurer. What does this mean?",
      options: [
        "The producer is now an employee of the insurer",
        "The insurer has authorized the producer to act as its agent",
        "The producer owns part of the insurance company",
        "The producer no longer needs a state license"
      ],
      correct: 1,
      explanation: "An appointment is the authorization by an insurer for a licensed producer to represent that company and sell its products. It creates an agency relationship but does not make the producer an employee."
    },
    {
      question: "What must an insurer do if it terminates a producer's appointment 'for cause'?",
      options: [
        "Nothing; terminations are private matters",
        "Notify the Director of Insurance within 30 days with the reasons",
        "Pay the producer severance",
        "Wait until the license renewal period"
      ],
      correct: 1,
      explanation: "If an insurer terminates an appointment for cause (misconduct, fraud, etc.), the insurer must notify the Director of Insurance within 30 days and provide the reasons for termination."
    },
    {
      question: "What is 'commingling' in insurance?",
      options: [
        "Selling insurance with other products",
        "Having appointments with multiple companies",
        "Mixing premium funds with personal or business operating funds",
        "Combining multiple policies into one"
      ],
      correct: 2,
      explanation: "Commingling is mixing premium trust funds with personal or business operating funds. This is prohibited because it makes accounting difficult and can lead to misuse of client funds."
    },
    {
      question: "Under the Gramm-Leach-Bliley Act, insurance agents must:",
      options: [
        "Share all client information with competitors",
        "Protect nonpublic personal information and provide privacy notices",
        "Report all client activities to the federal government",
        "Refuse to collect any personal information"
      ],
      correct: 1,
      explanation: "GLBA requires insurance agents to protect nonpublic personal information (NPI) and provide privacy notices explaining how information is collected, used, and protected."
    },
    {
      question: "Which of the following is an acceptable marketing practice for crop insurance?",
      options: [
        "Paying for a farmer's dinner to discuss insurance",
        "Giving a promotional pen with your contact information",
        "Offering to pay part of the premium",
        "Giving a gift card for policy referrals"
      ],
      correct: 1,
      explanation: "Promotional items of nominal value (like pens, calendars) are acceptable. Paying for meals, rebating premium, or giving substantial gifts are all prohibited inducements."
    },
    {
      question: "If an agent fails to obtain coverage requested by a client and doesn't notify the client, the agent may be:",
      options: [
        "Commended for not pressuring the client",
        "Liable for damages from the coverage gap",
        "Required to provide coverage at their own expense",
        "Given a warning but no penalty"
      ],
      correct: 1,
      explanation: "If an agent fails to obtain requested coverage and doesn't notify the client, the agent may be liable for damages the client suffers because they believed they had coverage they didn't have."
    },
    {
      question: "What is the purpose of administrative hearings in insurance licensing?",
      options: [
        "To determine premium rates",
        "To give licensees due process before license action is taken",
        "To approve new insurance products",
        "To train new producers"
      ],
      correct: 1,
      explanation: "Administrative hearings provide due process, giving licensees notice of proposed actions, the opportunity to present evidence, and a fair hearing before a license can be denied, suspended, or revoked."
    },
    {
      question: "A crop insurance agent is subject to regulation by:",
      options: [
        "Only the state Division of Insurance",
        "Only the federal RMA/FCIC",
        "Both state insurance regulators and federal RMA/FCIC",
        "Neither; crop insurance is unregulated"
      ],
      correct: 2,
      explanation: "Crop insurance agents face dual regulation: the state regulates licensing and conduct while federal RMA/FCIC sets policy terms and program rules. Both apply simultaneously."
    },
    {
      question: "Which action could result in license revocation in South Dakota?",
      options: [
        "Having appointments with multiple insurance companies",
        "Completing continuing education early",
        "Misappropriation of premium funds",
        "Offering multiple coverage options to clients"
      ],
      correct: 2,
      explanation: "Misappropriation (theft or misuse) of premium funds is a serious violation that can result in license revocation, criminal prosecution, and other penalties."
    },
    {
      question: "What is 'controlled business'?",
      options: [
        "Insurance sold in a regulated market",
        "Insurance written primarily on the producer's own property, family, or employer",
        "Business controlled by the federal government",
        "Insurance sold with other financial products"
      ],
      correct: 1,
      explanation: "Controlled business is insurance written primarily on the producer's own life/property, immediate family, employer, or employees. A license obtained mainly to write controlled business may be denied or revoked."
    },
    {
      question: "How should records containing client information be disposed of?",
      options: [
        "Thrown in regular trash",
        "Left for the next occupant if moving offices",
        "Securely destroyed (shredded)",
        "Sold to other agents"
      ],
      correct: 2,
      explanation: "Records containing sensitive client information must be securely disposed of (shredded or otherwise destroyed) to protect client privacy and comply with privacy laws."
    },
    {
      question: "What is the standard of proof typically used in administrative hearings?",
      options: [
        "Beyond a reasonable doubt",
        "Clear and convincing evidence",
        "Preponderance of the evidence",
        "Absolute certainty"
      ],
      correct: 2,
      explanation: "Administrative hearings typically use the 'preponderance of the evidence' standard, meaning the evidence shows it is more likely than not that the violation occurred."
    },
    {
      question: "An agent who repeatedly replaces clients' policies primarily to generate commissions is guilty of:",
      options: [
        "Good salesmanship",
        "Churning",
        "Legitimate competition",
        "Cross-selling"
      ],
      correct: 1,
      explanation: "Churning is excessive policy replacement primarily to generate commissions rather than benefit the client. It is an unfair trade practice similar to twisting."
    },
    {
      question: "When must a producer report a criminal conviction to the Director of Insurance?",
      options: [
        "Only if asked during license renewal",
        "Within 30 days of the conviction",
        "Never; criminal records are private",
        "Only for insurance-related crimes"
      ],
      correct: 1,
      explanation: "Producers must report criminal charges or convictions to the Director within 30 days. Failure to report is itself a violation that can result in disciplinary action."
    },
    {
      question: "What is 'binding authority'?",
      options: [
        "The authority to deny all claims",
        "The authority to accept risks and commit the insurer to provide coverage",
        "The authority to set premium rates",
        "The authority to license other agents"
      ],
      correct: 1,
      explanation: "Binding authority is the power given to an agent to accept risks and commit the insurer to provide coverage before the policy is actually issued."
    },
    {
      question: "An emergency license suspension may occur:",
      options: [
        "Only after a full administrative hearing",
        "Without prior hearing if public protection requires immediate action",
        "Only with the agent's consent",
        "Never; all suspensions require hearings first"
      ],
      correct: 1,
      explanation: "Emergency suspension may occur without prior hearing when public protection requires immediate action. A hearing would then follow the emergency action."
    },
    {
      question: "If a producer self-reports a violation before it's discovered:",
      options: [
        "The penalty is automatically doubled",
        "The violation is ignored",
        "It may result in lesser penalties",
        "The producer loses all licenses immediately"
      ],
      correct: 2,
      explanation: "Voluntary self-reporting of violations before discovery often results in lesser penalties. Attempting to cover up violations typically makes matters worse."
    },
    {
      question: "Which federal law requires insurance agents to provide privacy notices?",
      options: [
        "Federal Crop Insurance Act",
        "Gramm-Leach-Bliley Act",
        "Sarbanes-Oxley Act",
        "Dodd-Frank Act"
      ],
      correct: 1,
      explanation: "The Gramm-Leach-Bliley Act (GLBA) requires financial institutions, including insurance companies and agents, to provide privacy notices and protect nonpublic personal information."
    },
    {
      question: "A non-resident producer applying for a South Dakota license must:",
      options: [
        "Move to South Dakota",
        "Take the South Dakota licensing exam regardless of other licenses",
        "Hold a valid license in their home state",
        "Pay triple the licensing fees"
      ],
      correct: 2,
      explanation: "Non-resident producers must hold a valid license in their home state. If that state has reciprocity with South Dakota, they may obtain a South Dakota license without examination."
    },
    {
      question: "The purpose of the state licensing examination is to:",
      options: [
        "Generate revenue for the state",
        "Test knowledge of insurance concepts, laws, and ethics",
        "Limit the number of agents in the state",
        "Verify the applicant's credit score"
      ],
      correct: 1,
      explanation: "The licensing exam tests knowledge of insurance concepts, policy types, South Dakota laws and regulations, and ethics to ensure competence before licensing."
    },
    {
      question: "False advertising in insurance includes:",
      options: [
        "Accurate policy comparisons",
        "Statements that are untrue, deceptive, or misleading",
        "Factual rate information",
        "Educational materials about coverage options"
      ],
      correct: 1,
      explanation: "False advertising means making, publishing, or disseminating statements that are untrue, deceptive, or misleading about insurance products or services."
    },
    {
      question: "Insurance producer licenses in South Dakota are issued for a term of:",
      options: [
        "One year",
        "Two years",
        "Three years",
        "Five years"
      ],
      correct: 1,
      explanation: "South Dakota insurance producer licenses are issued for a two-year term and must be renewed before expiration to remain active."
    },
    {
      question: "Which of the following is NOT a ground for license denial?",
      options: [
        "Conviction of a felony",
        "Misrepresentation on the license application",
        "Changing insurance companies",
        "Obtaining a license through fraud"
      ],
      correct: 2,
      explanation: "Changing insurance companies (appointments) is normal business practice and not grounds for license denial. Criminal history, fraud, and misrepresentation are grounds for denial."
    },
    {
      question: "Defamation in insurance refers to:",
      options: [
        "Accurate comparisons between insurers",
        "Making false statements that damage the reputation of another insurer or agent",
        "Advertising your own services",
        "Explaining policy exclusions"
      ],
      correct: 1,
      explanation: "Defamation is making false statements that damage the reputation of another insurer, agent, or their products. It is a prohibited unfair trade practice."
    }
  ]
};

export default chapter7;
